import { useState } from 'react';

import {
  Flex,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Tag,
  useBoolean,
} from '@chakra-ui/react';
import { noop } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';

import { ArrowDown, ChevronDown } from 'assets/assetsV1/iconComponents';

import { convertDateToString2 } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';

import { TextBodyMedium, TextHeader2 } from 'uiToolkitV1/Typography';

import { DateRangePicker } from './index';

const formattedDateRange = (dateRange) => {
  if (dateRange.label) return dateRange.label;

  if (dateRange?.startDate) {
    return `${convertDateToString2(dateRange?.startDate)} - ${convertDateToString2(dateRange?.endDate)}`;
  }
  return 'All';
};

const DateRangeDropdown = ({
  onChange = noop,
  initialDateRange = {},
  minDate,
  maxDate,
  definedRanges,
  placeholderText = 'Select Date Range',
  onClose = noop,
  onClear = noop,
  onSelect = noop,
  showPredefinedRanges = false,
  getTimestamp = false,
  enableEmptyDateRangeToBeSelected = false,
  variant = 'default',
}) => {
  const [isOpen, setIsOpen] = useBoolean();
  const [currentDateRange, setCurrentDateRange] = useState({ ...initialDateRange });
  const [dateRange, setDateRange] = useState({ ...initialDateRange });

  const isChipVariant = variant === 'chip';

  return (
    <Popover isOpen={isOpen} preventOverflow placement="bottom" isLazy>
      <PopoverAnchor>
        <PopoverTrigger>
          <Flex alignItems="center">
            {isChipVariant ? (
              <Tag
                size="md"
                borderRadius="full"
                variant="solid"
                onClick={(e) => {
                  setIsOpen.toggle();
                  e.stopPropagation();
                }}
                cursor="pointer"
                sx={{
                  background: isOpen ? '#CAE8F0' : '#f3f4f6',
                  padding: '4px 10px',
                  border: isOpen ? '1px solid #004F67' : '1px solid transparent',
                }}
              >
                <TextBodyMedium bold color="text.g2" userSelect="none">
                  Timeline : &nbsp;
                </TextBodyMedium>
                <TextBodyMedium bold color="text.g1" userSelect="none">
                  {isEmpty(dateRange)
                    ? // biome-ignore lint/style/noNestedTernary: No explanation provided in original eslint-disable
                      enableEmptyDateRangeToBeSelected
                      ? 'All'
                      : 'Select'
                    : formattedDateRange(dateRange)}
                </TextBodyMedium>
                <ChevronDown
                  color="text.g1"
                  boxSize="16px"
                  ml="4px"
                  sx={{
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </Tag>
            ) : (
              <Flex
                sx={{
                  cursor: 'pointer',
                  borderBottom: isOpen ? '1px solid  #1565bc' : '1px dashed #999999',
                  transition: 'border-color 0.3s ease',
                  '&:hover': {
                    borderBottom: '1px solid #333333 !important',
                  },
                }}
                alignItems="center"
                onClick={(e) => {
                  setIsOpen.toggle();
                  e.stopPropagation();
                }}
              >
                <TextHeader2 bold>
                  {isEmpty(dateRange)
                    ? // biome-ignore lint/style/noNestedTernary: No explanation provided in original eslint-disable
                      enableEmptyDateRangeToBeSelected
                      ? 'All'
                      : 'Select'
                    : formattedDateRange(dateRange)}
                </TextHeader2>
                <ArrowDown color="text.g1" boxSize="20px" />
              </Flex>
            )}
          </Flex>
        </PopoverTrigger>
      </PopoverAnchor>
      <Portal>
        <PopoverContent sx={{ width: isMobileView() ? '100vw' : '100%', left: isMobileView() ? 0 : undefined }}>
          <DateRangePicker
            open={isOpen}
            onClose={() => {
              setIsOpen.off();
              onClose();
            }}
            onChange={(newDateRange) => {
              onChange(newDateRange);
              setCurrentDateRange(newDateRange);
            }}
            initialDateRange={dateRange}
            minDate={minDate}
            maxDate={maxDate}
            definedRanges={definedRanges}
            placeholderText={placeholderText}
            onClear={() => {
              if (enableEmptyDateRangeToBeSelected) {
                setCurrentDateRange({});
              }
              onClear();
            }}
            onSelect={() => {
              const dateTS = {
                startDate: currentDateRange?.startDate?.valueOf(),
                endDate: currentDateRange?.endDate?.valueOf(),
                label: currentDateRange?.label,
              };
              setDateRange(currentDateRange);
              if (getTimestamp) {
                onSelect(dateTS);
              } else {
                onSelect(currentDateRange);
              }
              setIsOpen.off();
            }}
            showPredefinedRanges={showPredefinedRanges}
            enableEmptyDateRangeToBeSelected={enableEmptyDateRangeToBeSelected}
            displayTwoMonthsLayout={!isMobileView()}
          />
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export { DateRangeDropdown };
